import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Share2, Copy, Check, MessageCircle, Twitter, Mail, Link2 } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";

/**
 * Invite dialog: share the FaithCycle app itself (not a specific verse).
 * Uses the current origin as the invite link.
 */
export default function InviteDialog({ open, onOpenChange }) {
  const { lang } = useApp();
  const [copied, setCopied] = useState(null);
  const T = (en, es) => (lang === "es" ? es : en);

  const appUrl = window.location.origin;
  const invitationText = T(
    `I've been using FaithCycle — a bilingual Bible app with reading plans, study notes and a quiet community. Try it here:\n${appUrl}`,
    `He estado usando FaithCycle — una app de Biblia bilingüe con planes de lectura, notas de estudio y una comunidad serena. Pruébala aquí:\n${appUrl}`,
  );

  const doCopy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1800);
      toast.success(T("Copied to clipboard", "Copiado al portapapeles"));
    } catch {
      toast.error(T("Copy failed. Long-press to copy.", "No se pudo copiar. Mantén presionado para copiar."));
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "FaithCycle",
          text: invitationText,
          url: appUrl,
        });
      } catch (e) {
        if (e.name !== "AbortError") toast.error(T("Share cancelled", "Compartir cancelado"));
      }
    } else {
      doCopy(invitationText, "share");
    }
  };

  const openWhatsapp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(invitationText)}`, "_blank", "noopener");
  };

  const openTwitter = () => {
    const tweetText = T(
      "I'm reading the Bible in a calmer place — FaithCycle 🌿",
      "Estoy leyendo la Biblia en un lugar más sereno — FaithCycle 🌿",
    );
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(appUrl)}`,
      "_blank",
      "noopener",
    );
  };

  const openEmail = () => {
    const subject = T("Try FaithCycle — a calm Bible app", "Prueba FaithCycle — una app de Biblia serena");
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(invitationText)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="invite-dialog"
        className="bg-[var(--surface-elevated)] border-[var(--border)] max-w-md"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-[var(--accent)]">
            <Share2 className="w-4 h-4" strokeWidth={1.5} />
            <span className="uppercase tracking-[0.3em] text-[10px]">
              {T("Invite a friend", "Invita a un amigo")}
            </span>
          </div>
          <DialogTitle className="font-serif text-2xl md:text-3xl tracking-tight">
            {T("Share FaithCycle", "Comparte FaithCycle")}
          </DialogTitle>
        </DialogHeader>

        {/* Preview */}
        <div
          data-testid="invite-preview"
          className="rounded-2xl bg-gradient-to-br from-[var(--highlight)]/60 to-[var(--surface)] border border-[var(--border)] p-6"
        >
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[var(--accent)]"></span>
            <span className="font-serif text-2xl tracking-tight">FaithCycle</span>
          </div>
          <p className="mt-3 font-serif-body text-base leading-relaxed text-[var(--text-primary)]">
            {T(
              "A quieter place to read scripture — bilingual Bible, study notes and a thoughtful community.",
              "Un lugar más sereno para leer las Escrituras — Biblia bilingüe, notas de estudio y una comunidad pausada.",
            )}
          </p>
          <p className="mt-4 text-xs text-[var(--accent)] break-all">{appUrl}</p>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button
            data-testid="invite-native-btn"
            onClick={nativeShare}
            className="rounded-full h-11 bg-[var(--accent)] hover:bg-[var(--accent)]/90 text-white col-span-2"
          >
            <Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
            {T("Share via device", "Compartir con el dispositivo")}
          </Button>
          <Button
            data-testid="invite-whatsapp-btn"
            variant="outline"
            onClick={openWhatsapp}
            className="rounded-full h-11 border-[var(--border)] hover:bg-[var(--surface)]"
          >
            <MessageCircle className="w-4 h-4 mr-2" strokeWidth={1.5} />
            WhatsApp
          </Button>
          <Button
            data-testid="invite-twitter-btn"
            variant="outline"
            onClick={openTwitter}
            className="rounded-full h-11 border-[var(--border)] hover:bg-[var(--surface)]"
          >
            <Twitter className="w-4 h-4 mr-2" strokeWidth={1.5} />
            X / Twitter
          </Button>
          <Button
            data-testid="invite-email-btn"
            variant="outline"
            onClick={openEmail}
            className="rounded-full h-11 border-[var(--border)] hover:bg-[var(--surface)]"
          >
            <Mail className="w-4 h-4 mr-2" strokeWidth={1.5} />
            {T("Email", "Correo")}
          </Button>
          <Button
            data-testid="invite-copy-link-btn"
            variant="outline"
            onClick={() => doCopy(appUrl, "link")}
            className="rounded-full h-11 border-[var(--border)] hover:bg-[var(--surface)]"
          >
            {copied === "link"
              ? <Check className="w-4 h-4 mr-2" strokeWidth={1.5} />
              : <Link2 className="w-4 h-4 mr-2" strokeWidth={1.5} />}
            {T("Copy link", "Copiar enlace")}
          </Button>
        </div>

        <Button
          data-testid="invite-copy-message-btn"
          variant="ghost"
          onClick={() => doCopy(invitationText, "text")}
          className="w-full rounded-full h-10 text-[var(--text-secondary)] hover:text-[var(--accent)]"
        >
          {copied === "text"
            ? <Check className="w-4 h-4 mr-2" strokeWidth={1.5} />
            : <Copy className="w-4 h-4 mr-2" strokeWidth={1.5} />}
          {T("Copy full message", "Copiar mensaje completo")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
